import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import {
  facilitiesService,
  rolesService,
  usersService,
  locationsService,
  physiciansService,
  practicesService,
  sourcesService,
  sourceSubsitesService,
  stainsService,
  workflowsService,
} from "../services";

// Facilities
export function useFacilities() {
  return useQuery({
    queryKey: ["facilities"],
    queryFn: () => facilitiesService.getAll(),
  });
}

export function useActiveFacilities() {
  return useQuery({
    queryKey: ["facilities", "active"],
    queryFn: () => facilitiesService.getActive(),
  });
}

// Roles
export function useRoles() {
  return useQuery({
    queryKey: ["roles"],
    queryFn: () => rolesService.getAll(),
  });
}

export function useActiveRoles() {
  return useQuery({
    queryKey: ["roles", "active"],
    queryFn: () => rolesService.getActive(),
  });
}

// Users
export function useUsers() {
  return useQuery({
    queryKey: ["users"],
    queryFn: () => usersService.getAll(),
  });
}

export function useActiveUsers() {
  return useQuery({
    queryKey: ["users", "active"],
    queryFn: () => usersService.getActive(),
  });
}

export function usePathologists() {
  return useQuery({
    queryKey: ["users", "pathologists"],
    queryFn: () => usersService.getPathologists(),
  });
}

// Practices (v2: replaces accounts)
export function useAccounts() {
  return useQuery({
    queryKey: ["practices"],
    queryFn: () => practicesService.getAll(),
  });
}

// Locations
export function useLocations() {
  return useQuery({
    queryKey: ["locations"],
    queryFn: () => locationsService.getAll(),
  });
}

export function useLocationsByAccount(accountId: string | undefined) {
  return useQuery({
    queryKey: ["locations", "account", accountId],
    queryFn: () => locationsService.getByAccountId(accountId!),
    enabled: !!accountId,
  });
}

// Physicians
export function usePhysicians() {
  return useQuery({
    queryKey: ["physicians"],
    queryFn: () => physiciansService.getAll(),
  });
}

// Sources
export function useSources() {
  return useQuery({
    queryKey: ["sources"],
    queryFn: () => sourcesService.getAll(),
  });
}

// Source Subsites
export function useSourceSubsites() {
  return useQuery({
    queryKey: ["source-subsites"],
    queryFn: () => sourceSubsitesService.getAll(),
  });
}

export function useSubsitesBySource(sourceId: string | undefined) {
  return useQuery({
    queryKey: ["source-subsites", "source", sourceId],
    queryFn: () => sourceSubsitesService.getBySourceId(sourceId!),
    enabled: !!sourceId,
  });
}

// Stains
export function useStains() {
  return useQuery({
    queryKey: ["stains"],
    queryFn: () => stainsService.getAll(),
  });
}

// Workflows
export function useWorkflows() {
  return useQuery({
    queryKey: ["workflows"],
    queryFn: () => workflowsService.getAll(),
  });
}

export function useCaseStatuses() {
  return useQuery({
    queryKey: ["case-statuses"],
    queryFn: () => workflowsService.getCaseStatuses(),
  });
}

export function useCaseStatusesForFiltering() {
  return useQuery({
    queryKey: ["case-statuses-for-filtering"],
    queryFn: () => workflowsService.getCaseStatusesForFiltering(),
    staleTime: 1000 * 60 * 60, // 1 hour - statuses rarely change
  });
}

export function useWorkflowStatuses(workflowId: string | undefined) {
  return useQuery({
    queryKey: ["workflow-statuses", workflowId],
    queryFn: () => workflowsService.getWorkflowStatuses(workflowId!),
    enabled: !!workflowId,
  });
}

// Generic mutation hooks for CRUD operations
export function useGenericCreate<T, D>(
  queryKey: string,
  createFn: (data: D) => Promise<T>,
  entityName: string
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
      notifications.show({
        title: "Success",
        message: `${entityName} created successfully`,
        color: "green",
      });
    },
    onError: (error: unknown) => {
      const responseMessage = (
        error as { response?: { data?: { message?: string } } }
      )?.response?.data?.message;
      notifications.show({
        title: "Error",
        message: responseMessage || `Failed to create ${entityName}`,
        color: "red",
      });
    },
  });
}

export function useGenericUpdate<T, D>(
  queryKey: string,
  updateFn: (id: string, data: D) => Promise<T>,
  entityName: string
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: D }) => updateFn(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
      notifications.show({
        title: "Success",
        message: `${entityName} updated successfully`,
        color: "green",
      });
    },
    onError: (error: unknown) => {
      const responseMessage = (
        error as { response?: { data?: { message?: string } } }
      )?.response?.data?.message;
      notifications.show({
        title: "Error",
        message: responseMessage || `Failed to update ${entityName}`,
        color: "red",
      });
    },
  });
}

export function useGenericDelete(
  queryKey: string,
  deleteFn: (id: string) => Promise<void>,
  entityName: string
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
      notifications.show({
        title: "Success",
        message: `${entityName} deleted successfully`,
        color: "green",
      });
    },
    onError: (error: unknown) => {
      const responseMessage = (
        error as { response?: { data?: { message?: string } } }
      )?.response?.data?.message;
      notifications.show({
        title: "Error",
        message: responseMessage || `Failed to delete ${entityName}`,
        color: "red",
      });
    },
  });
}
